import React, {useState} from 'react';
import {Card, ListGroup} from 'react-bootstrap';
import {BaseModal} from 'components/modals';
import Col from 'react-bootstrap/Col';
import {TextSamplesModalContentsWrapper} from './modals/TextSamplesModalContentsWrapper';
import CellLoader from '@/components/loading/CellLoader';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {ChartFieldToQueryOption} from 'entities/chart';
import {TextSamplesChartData} from 'entities/chartData';
import {EventTextField, EventTextFieldOption} from 'entities/event';
import i18n from 'i18n';
import {useFormattedDate} from '@/components/utils/FormattedDateTime';
import {buildEventDetailURL, buildEventURL} from '@/utils/urlBuilder';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import EventQuickViewHeader from '@/views/event_detail/EventQuickViewHeader';
import useData from '@/utils/useData';
import {EventDetails} from '@/types/event';
import {EventActorsList} from '@/views/event_detail/EventActorsList';

interface Props {
    fieldToQuery: ChartFieldToQueryOption;
    previewOnly: boolean;
    data: TextSamplesChartData[];
    queryDuration?: string;
    chartSettingsView?: boolean;
}


const TextSamplesWrapper = React.memo<Props>(
    ({data, fieldToQuery, previewOnly, queryDuration = '0', chartSettingsView = false}) => {
        const {formatDate} = useFormattedDate();
        const {userData, loading: loadingUserData} = useOptimizedConfig();

        const [modalShown, setModalShown] = useState<boolean>(false);
        const [activeEventId, setActiveEventId] = useState<number | null>(null);
        const [activeEvent, setActiveEvent] = useState<TextSamplesChartData | null>(null);

        const openDetailModal = (x: TextSamplesChartData) => {
            if (!previewOnly) {
                setActiveEventId(x.event_id);
                setActiveEvent(x);
                setModalShown(true);
            }
        };

        let fieldName: EventTextField;
        if (fieldToQuery === EventTextFieldOption.AllFields) {
            fieldName = EventTextFieldOption.EventText;
        } else {
            fieldName = fieldToQuery;
        }

        let modalContents;
        if (modalShown && !previewOnly && activeEvent) {
            modalContents = (
                <TextSamplesModalContentsWrapper
                    eventData={activeEvent}
                    fieldToShow={fieldName}
                    autoScrollScope="textSamples"
                />
            );
        }

        // Load full event details for richer header (channel, agent, customer)
        const [eventDetails] = useData<EventDetails | null>(
            activeEventId ? buildEventURL('eventDetails', activeEventId) : null,
            {
                useLocalStore: true,
                raiseErrors: false,
            },
        );

        let headerChannelName: string | undefined;
        let headerAgent: string | undefined;
        let headerCustomer: string | undefined;

        if (eventDetails) {
            const {eventAgent, eventCustomer} = EventActorsList(eventDetails as any, null, userData, [...(userData?.restrictedFieldKeys || []), ...(eventDetails?._restricted_fields || [])]);
            headerChannelName = eventDetails.channel?.name;

            if (eventAgent) {
                if (typeof eventAgent === 'string') {
                    headerAgent = eventAgent;
                } else if (Array.isArray(eventAgent)) {
                    headerAgent = eventAgent[0]?.agentName || '';
                } else {
                    // single agent object
                    // @ts-expect-error runtime field
                    headerAgent = (eventAgent.agentName || eventAgent.fullName || '').toString();
                }
            }

            if (eventCustomer) {
                headerCustomer = (eventCustomer.customerName || '').trim();
            }
        }

        let previewClass = '';
        if (previewOnly) {
            previewClass = 'preview-only';
        }

        const userCanAccessEventDetailView = hasPermission('fs.event.list', userData?.permissions, true);

        const truncate = (str: any, maxLength: number) => {
            if (str) {
                return str.toString().length > maxLength ? str.toString().slice(0, maxLength) + '...' : str.toString();
            }
            else {
                return '';
            }
        };

        const formatMetadataValue = (value: string | undefined) => {
            if (!value) {
                return '';
            }

            return value.charAt(0).toUpperCase() + value.slice(1);
        };

        const buildSampleMetadata = (obj: TextSamplesChartData) => [
            `Event id: ${obj.event_id}`,
            formatMetadataValue(obj.event_type_name),
            formatDate(obj.event_datetime),
        ].filter(Boolean);

        if (!loadingUserData) {
            return (
                <div
                    className="w-100 h-100 text-center text-samples-wrapper chart-concordance-samples-container">
                    <BaseModal
                        isOpen={modalShown}
                        onClose={() => setModalShown(false)}
                        title=""
                        hideHeader={true}
                    >
                        <EventQuickViewHeader
                            eventType={activeEvent?.event_type_name}
                            eventId={activeEventId?.toString() || ''}
                            channelName={headerChannelName}
                            dateTime={activeEvent?.event_datetime}
                            eventAgent={headerAgent}
                            eventCustomer={headerCustomer}
                            canOpenDetailView={userCanAccessEventDetailView && !!activeEventId}
                            detailUrl={activeEventId ? buildEventDetailURL(activeEventId) : undefined}
                            onClose={() => setModalShown(false)}
                        />
                        <hr className="my-0" />
                        <div className="modal-body">
                            <Col>
                                {modalContents}
                            </Col>
                        </div>
                    </BaseModal>

                    <Card className="overflow-hidden text-samples-card">
                        <ListGroup className="text-samples">
                            {data.map((obj) => {
                                const metadata = buildSampleMetadata(obj);

                                return (
                                    <ListGroup.Item
                                        className={`text-sample-element ${previewClass}`}
                                        key={obj.event_id}
                                        onClick={previewOnly ? undefined : () => openDetailModal(obj)}
                                    >
                                        <div className="text-sample-element__text">
                                            {truncate(obj[fieldName as keyof TextSamplesChartData], 200)}
                                        </div>
                                        {metadata.length ? (
                                            <div className="text-sample-element__metadata">
                                                {metadata.map((item, index) => (
                                                    <React.Fragment key={`${obj.event_id}-${item}`}>
                                                        {index > 0 ? <span className="text-sample-element__separator">·</span> : null}
                                                        <span>{item}</span>
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        ) : null}
                                    </ListGroup.Item>
                                );
                            })}
                        </ListGroup>
                    </Card>
                    {!chartSettingsView ? (
                        <div className="chart-query-time-indicator">
                            <p>
                                {i18n.t('table_chart_query_duration.text', {queryDuration})}
                            </p>
                        </div>
                    ) : null}
                </div>
            );
        } else {
            return (
                <CellLoader fullHeight={false} />
            );
        }
    },
);


export default TextSamplesWrapper;
